import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Briefcase, Flame, ArrowRight, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  {
    icon: FileText,
    title: 'Upload Your CV',
    description: 'Drop your CV. JobSurfAI reads your skills, experience and background so it can match you to the right roles.',
    cta: 'Upload CV',
    route: '/cv',
    color: 'from-indigo-500 to-blue-600',
    bgAccent: 'bg-indigo-500/10',
    textAccent: 'text-indigo-400',
  },
  {
    icon: Briefcase,
    title: 'Discover & Match',
    description: 'Pick your roles and locations. We find relevant jobs and show how well each one matches your profile.',
    cta: 'See Job Feed',
    route: '/jobs',
    color: 'from-teal-500 to-emerald-600',
    bgAccent: 'bg-teal-500/10',
    textAccent: 'text-teal-400',
  },
  {
    icon: Flame,
    title: 'Swipe, Tailor, Apply, Track',
    description: 'Swipe right on jobs you like. Tailor your CV and cover letter with AI, review them, apply on the employer\'s site and track every application here.',
    cta: 'Start Swiping',
    route: '/swipe',
    color: 'from-orange-500 to-rose-600',
    bgAccent: 'bg-orange-500/10',
    textAccent: 'text-orange-400',
  },
];

export default function OnboardingModal({ onComplete }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const markComplete = async () => {
    try {
      await axios.post(`${API}/user/onboarding/complete`, null, { headers: getAuthHeaders() });
    } catch (e) {
      // non-blocking
    }
    onComplete();
  };

  const handleCTA = () => {
    markComplete();
    navigate(STEPS[step].route);
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      handleCTA();
    }
  };

  const handleSkip = () => {
    markComplete();
  };

  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="onboarding-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="relative w-full max-w-lg bg-[#111111] border border-white/[0.08] rounded-3xl overflow-hidden shadow-2xl shadow-black/50"
      >
        {/* Skip button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 z-10 text-zinc-500 hover:text-zinc-300 transition-colors"
          data-testid="onboarding-skip-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Step indicator dots */}
        <div className="flex items-center justify-center gap-2 pt-6">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === step ? 'w-8 bg-white' : i < step ? 'w-4 bg-white/40' : 'w-4 bg-white/15'
              }`}
            />
          ))}
        </div>

        {/* Content area */}
        <div className="px-8 pt-8 pb-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.25 }}
              className="flex flex-col items-center text-center"
            >
              {/* Icon */}
              <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${current.color} flex items-center justify-center mb-6 shadow-lg`}>
                <Icon className="w-10 h-10 text-white" strokeWidth={1.5} />
              </div>

              {/* Step number */}
              <span className="text-xs font-bold tracking-widest uppercase text-zinc-500 mb-2">
                Step {step + 1} of {STEPS.length}
              </span>

              {/* Title */}
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">
                {current.title}
              </h2>

              {/* Description */}
              <p className="text-zinc-400 text-sm sm:text-base leading-relaxed max-w-sm mb-8">
                {current.description}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Action buttons */}
          <div className="flex flex-col gap-3">
            {step < STEPS.length - 1 ? (
              <>
                <Button
                  onClick={handleNext}
                  className="w-full bg-white text-black hover:bg-zinc-200 rounded-xl py-6 text-base font-semibold transition-all hover:scale-[1.02]"
                  data-testid="onboarding-next-btn"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleCTA}
                  className={`w-full ${current.textAccent} hover:bg-white/[0.05] rounded-xl py-5 text-sm font-medium`}
                  data-testid="onboarding-cta-btn"
                >
                  {current.cta}
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </>
            ) : (
              <Button
                onClick={handleCTA}
                className={`w-full bg-gradient-to-r ${current.color} text-white hover:opacity-90 rounded-xl py-6 text-base font-semibold transition-all hover:scale-[1.02]`}
                data-testid="onboarding-start-btn"
              >
                {current.cta}
                <Flame className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>

          {/* Skip text + consent */}
          <p className="text-[10px] text-zinc-600 text-center mt-3 px-2 leading-relaxed">
            By continuing, you agree to our <a href="/terms" className="text-indigo-400 hover:underline">Terms of Service</a> and <a href="/privacy" className="text-indigo-400 hover:underline">Privacy Policy</a>, including the use of AI to process your data and generate application materials.
          </p>
          <button
            onClick={handleSkip}
            className="w-full text-center text-xs text-zinc-600 hover:text-zinc-400 mt-3 transition-colors"
            data-testid="onboarding-skip-text-btn"
          >
            Skip setup — I'll explore on my own
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
